import { Toaster } from "@clarkinc/zest-design-system/ui";
import whenDomReady from "when-dom-ready";
import { polyfills } from "./polyfills";
import Mount from "./common/Mount";
import Header from "./common/Header";
import { loadUserAnalytics } from "./gtm";
import renderFromPreloadedJSON from "./common/renderFromPreloadedJSON";
import tracker from "./tracker";
import userPageHistoryTracking from "./userPageHistoryTracking";
import ajaxToken from "./ajaxToken";
import { logException } from "./__alias__/error-tracking";
import { env } from "./common/Environment";
import { initSentry } from "./__alias__/error-tracking/sentry";
/** Used in conjunction with SWC and BrowserList
 * @see {@link https://swc.rs/docs/configuration/supported-browsers#mode SWC Docs}
 */
import "core-js";
/** Manually added due to the following issue.
 * @see {@link https://github.com/swc-project/swc/issues/7083 Github Issue}
 */
import "core-js/actual/array/at";
import Analytics from "./analytics/Analytics";

initSentry("Legacy Page");

/** This maybe moved to a new entry that is only for the webpack running in watch mode */
if (module.hot) {
  module.hot.accept();
}

if (
  !window.location.pathname.startsWith("/blog") &&
  !window.location.pathname.startsWith("/careers")
) {
  import(
    /* webpackChunkName: "common-critical-styles" */ "~/less/_critical/include.less"
  );
  import(/* webpackChunkName: "tailwind" */ "~/css/tailwind.css");
}

if (window.location.href.includes("webcache.googleusercontent.com")) {
  import("./googleWebCache");
}

const loadGdprBanner = async () => {
  const gdprBannerMount = document.getElementById("gdprBannerMount");
  const { storageFactory } = await import("storage-factory");

  const local = storageFactory(() => window.localStorage);
  const session = storageFactory(() => window.sessionStorage);

  if (gdprBannerMount) {
    if (
      !local.getItem("data-policy-seen") &&
      !session.getItem("outside-of-european-union")
    ) {
      const { default: GdprBanner } = await import("./common/GdprBanner");
      Mount(GdprBanner, gdprBannerMount.dataset, gdprBannerMount);
    }
  }
};

const loadProductListingFilter = async () => {
  const filterBar = document.querySelector(".ag-filter");

  if (filterBar) {
    const { default: ProductListingFilter } = await import(
      "./common/ProductListingFilter"
    );
    new ProductListingFilter();
  }
};

const loadDynamicModules = () => {
  window.dynamicModules = window.dynamicModules || [];

  const modules = {
    customCategory: () => {
      import("./customCategory");
    },
    cart: () => {
      import("./cart/index");
    },
    checkout: () => {
      import("./checkout/index");
    },
    vendor: () => {
      import("./vendor");
    },
    orderTracking: () => {
      import("./my-account/orderTracking");
    },
    videoPlayer: () => {
      import("./common/videoPlayer");
    },
    productListing: () => {
      import(/* webpackChunkName: "productListing" */ "./productListing");
    },
    compatibleWith: () => {
      import("./compatible-with");
    },
    customDoors: () => {
      import("./custom-doors/index");
    },
    holiday: () => {
      import("./holiday");
    },
    myAccount: () => {
      import("./my-account/index");
    },
    myAccountRequestAdditionalFunds: () => {
      import("./my-account/orders/payment/index");
    },
    initWishList: () => {
      import("./WishListManager/initWishList");
    },
    viewinfoPayment: () => {
      import("./checkout/viewInfoCard/index");
    },
    viewinfoNewPayment: () => {
      import("./checkout/viewInfoNewPayment/index");
    },
    initSpecializedPage: () => {
      import("./productListing/initSpecializedPage");
    },
    initCategoryPage: () => {
      import("./CategoryPage/initCategoryPage");
    },
    vendorApplication: () => {
      import("./vendorApplication");
    },
    brochureVendorApplication: () => {
      import("./Brochure/VendorApplication");
    },
    specialCategory: () => {
      import("./jquery.special.category");
    },
    brandListing: () => {
      import("./Brands");
    },
    guestCheckout: () => {
      /* webpackChunkName: "guestCheckout" */ import("./checkout/guest");
    },
    initPriceChopperPage: () => {
      import("./productListing/PriceChopperPage/initPriceChopperPage");
    },
    initSearchPage: () => {
      import("./productListing/SearchPage/initSearchPage");
    },
    initPurchasingGroupPage: () => {
      import("./productListing/PurchasingGroupPage/initPurchasingGroupPage");
    },
    initCompareProductsPage: () => {
      import("./productListing/CompareProductsPage/initCompareProductsPage");
    },
    initCompatibleWithPage: () => {
      import("./productListing/CompatibleWithPage/initCompatibleWithPage");
    },
    initYouMayAlsoNeedPage: () => {
      import("./productListing/YouMayAlsoNeedPage/initYouMayAlsoNeedPage");
    },
    initPlusPage: () => {
      import("./PlusPage/initPlusPage");
    },
    initProductDetail: () => {
      import("./ProductDetails/initProductDetail");
    },
    initProductReviews: () => {
      import("./ProductReviews/initProductReviews");
    },
    initVideoModalContainer: () => {
      import("./common/VideoModalContainer/initVideoModalContainer");
    },
    initSupportPage: () => {
      import("./StaticContentPages/SupportPage");
    },
    initHelpBlock: () => {
      import("./common/Layout/HelpBlock/initHelpBlock");
    },
    specialOrdersRequest: () => {
      import("./Brochure/SpecialOrdersRequest");
    },
    scholarshipDashboard: () => {
      import("./Brochure/Scholarship/ScholarshipDashboard");
    },
    foodserviceHospitalityScholarship: () => {
      import("./Brochure/Scholarship/FoodserviceHospitalityScholarship");
    },
    diversityScholarship: () => {
      import("./Brochure/Scholarship/DiversityScholarship/index.legacy");
    },
    samplesRequest: () => {
      import("./Brochure/SamplesRequest");
    },
    blogCouponCodesSignup: () => {
      import("./Brochure/Blog/BlogCouponCodesSignup");
    },
    duplicateOrderModal: () => {
      import("./checkout/duplicateOrderModal/index.js");
    },
    blogHeader: () => {
      import("./Brochure/Blog/BlogHeader");
    },
    blogSocialMediaShareIcons: () => {
      import("./Brochure/Blog/BlogSocialMediaShareIcons");
    },
    initCheckoutPayment: () => {
      import("./FullCheckout/CheckoutPayment/init.js");
    },
    initOrderConfirmation: () => {
      import("./FullCheckout/CheckoutComplete/OrderConfirmation/init.js");
    },
    initSubmitToSupervisor: () => {
      import("./FullCheckout/CheckoutComplete/SubmitToSupervisor/init.js");
    },
    initReviewOrder: () => {
      import("./FullCheckout/CheckoutComplete/ReviewOrder/init.js");
    },
    initOrderConfirmationError: () => {
      import("./FullCheckout/CheckoutComplete/Error/init.js");
    },
    quoteRequest: () => {
      import("./Brochure/QuoteRequest");
    },
    beforeYouContact: () => {
      import("./Brochure/BeforeYouContact");
    },
    searchFrequentlyAskedQuestions: () => {
      import("./Brochure/SearchFrequentlyAskedQuestions");
    },
    secureUpload: () => {
      import("./Brochure/SecureUpload");
    },
    restaurantLayoutTool: () => {
      import("./Brochure/RestaurantLayoutTool/index.js");
    },
    supportPage: () => {
      import("./Brochure/Support");
    },
    foodServiceArticlesSearchResults: () => {
      import(
        "./Brochure/FoodServiceResources/FoodServiceResourcesSearchResults"
      );
    },
    foodServiceResourcesSearch: () => {
      import(
        "./Brochure/FoodServiceResources/FoodServiceResourcesSidebar/FoodServiceResourcesSearch"
      );
    },
    foodServiceResourcesSubscribe: () => {
      import(
        "./Brochure/FoodServiceResources/FoodServiceResourcesSidebar/FoodServiceResourcesSubscribe"
      );
    },
    foodServiceResourcesCategories: () => {
      import(
        "./Brochure/FoodServiceResources/FoodServiceResourcesSidebar/FoodServiceResourcesCategories"
      );
    },
    foodServiceResourcesHeader: () => {
      import("./Brochure/FoodServiceResources/FoodServiceResourcesHeader");
    },
    foodServiceResourcesIndex: () => {
      import("./Brochure/FoodServiceResources/FoodServiceResourcesHomepage");
    },
    foodServiceResourcesArticle: () => {
      import("./Brochure/FoodServiceResources/FoodServiceResourcesArticle");
    },
    foodServiceResourcesBuyingGuide: () => {
      import("./Brochure/FoodServiceResources/FoodServiceResourcesBuyingGuide");
    },
    foodServiceResourcesVideo: () => {
      import("./Brochure/FoodServiceResources/FoodServiceResourcesVideo");
    },
    foodServiceResourcesReviewPage: () => {
      import("./Brochure/FoodServiceResources/FoodServiceResourcesReviewPage");
    },
    blogHomepage: () => {
      import("./Brochure/Blog/BlogHomepage/index.legacy");
    },
    blogPostContent: () => {
      import("./Brochure/Blog/BlogPostContent/index.legacy");
    },
    careersIndex: () => {
      import("./Brochure/Careers/Index/index.legacy");
    },
    careersDepartment: () => {
      import("./Brochure/Careers/Department");
    },
    careersJobListing: () => {
      import("./Brochure/Careers/JobListing");
    },
    careersAlumni: () => {
      import("./Brochure/Careers/Alumni/index.legacy");
    },
    emailPreferences: () => {
      import("./Brochure/EmailPreferences");
    },
    recipeResizer: () => {
      import("./Brochure/RecipeResizer");
    },
    toastLandingPage: () => {
      import("./Brochure/ToastLanding");
    },
    initShippingBillingInfo: () => {
      import("./ShippingBillingInfo/initShippingBillingInfo");
    },
    initVersaHub: () => {
      import("./versahub/initVersaHub");
    },
    foodServiceResourcesTypeLanding: () => {
      import("./Brochure/FoodServiceResources/FoodServiceResourcesTypeLanding");
    },
    foodServiceResourcesCategoryLanding: () => {
      import(
        "./Brochure/FoodServiceResources/FoodServiceResourcesCategoryLanding"
      );
    },
    partsAndAccessories: () => {
      import("./Brochure/PartsAndAccessories");
    },
    initHomePage: () => {
      import("./HomePage/index.legacy");
    },
    initRapidReorder: () => {
      import("./RapidReorderManager/initRapidReorder");
    },
    initPageCreatorPage: () => {
      import("./PageCreatorPage/initPageCreatorPage");
    },
    initPageCreatorItemListingPage: () => {
      import(
        "./PageCreatorPage/ItemListingPage/initPageCreatorItemListingPage"
      );
    },
    initPageCreatorInactivePage: () => {
      import("./PageCreatorPage/InactivePage/initPageCreatorInactivePage");
    },
    initPageCreatorLeadGenerationPage: () => {
      import(
        "./PageCreatorPage/LeadGenerationPage/initPageCreatorLeadGenerationPage"
      );
    },
    initPagination: () => {
      import("./common/Pagination/initPagination");
    },
    initLeftNavbar: () => {
      import("./my-account/LeftNavbar/initLeftNavbar");
    },
    initInternalNotesContent: () => {
      import("./ProductDetails/InternalNotes/initInternalNotesContent");
    },
    initAltoShaamDemoPage: () => {
      import("./Brochure/AltoShaamDemoPage/initAltoShaamDemoPage");
    },
    initCurtronDoorPage: () => {
      import("./CurtronDoorPage/initCurtronDoorPage");
    },
    initMobilePaymentSelection: () => {
      import("./checkout/viewInfoCard/initMobilePaymentSelection");
    },
    initMobileApplePayButton: () => {
      import("./checkout/mobile/initMobileApplePayButton");
    },
    initFirstOrderPage: () => {
      import("./FirstOrderPage/initFirstOrderPage");
    },
    initCustomizableToolPage: () => {
      import("./CustomizableToolPage/initCustomizableToolPage");
    },
    initImageSearchPage: () => {
      import("./productListing/ImageSearchPage/initImageSearchPage");
    },
    initSurveyAlert: () => {
      import("./my-account/CurrentInstallations/SurveyAlert/initSurveyAlert");
    },
    initBlogFooter: () => {
      import("./Brochure/Blog/BlogFooter");
    },
    initExpiringCreditCards: () => {
      import("./my-account/Common/ExpiringCreditCards/initExpiringCreditCards");
    },
    initOrderHistory: () => {
      import("./my-account/OrderHistory/initOrderHistory");
    },
    initRecentOrders: () => {
      import("./my-account/Dashboard/RecentOrders/initRecentOrders");
    },
    initTrackOrder: () => {
      import("./my-account/TrackOrder/initTrackOrder");
    },
  };

  for (const name of window.dynamicModules) {
    if (!(name in modules)) {
      logException(
        new Error(
          `Key ${name} is in the modules object. Make sure it is supposed to be in the dynamicModules array`,
        ),
      );

      continue;
    }
    modules[name]();
  }

  for (const node of document.querySelectorAll("[data-module]")) {
    const name = node.getAttribute("data-module");
    if (!(name in modules)) {
      logException(
        new Error(
          `Key ${name} is not in the modules object. Make sure it is supposed to part of the data-module attribute.`,
        ),
      );

      continue;
    }
    modules[name]();
  }

  Mount(Analytics, {}, document.createElement("div"));
};

const hydrateHeader = () => {
  renderFromPreloadedJSON(Header, "GlobalHeader", true);
};

const hydrateFooter = async () => {
  if (document.querySelector('[data-hypernova-key="Footer"]')) {
    const { default: Footer } = await import("./common/Footer");
    renderFromPreloadedJSON(Footer, "Footer");
  }
};

const main = async () => {
  await polyfills;
  Mount(Toaster, {}, document.createElement("div"));
  if (document.querySelector('[data-hypernova-key="GlobalHeader"]')) {
    setTimeout(hydrateHeader);
  } else {
    document.addEventListener("hydrateHeader", () => setTimeout(hydrateHeader));
  }

  whenDomReady(() => {
    loadDynamicModules();

    setTimeout(loadGdprBanner);

    setTimeout(loadProductListingFilter);

    setTimeout(
      () => import(/* webpackChunkName: "recaptcha" */ "~/less/recaptcha.less"),
    );

    if (!window.location.pathname.includes("cloudflareerror")) {
      setTimeout(loadUserAnalytics);
    }

    setTimeout(tracker);

    setTimeout(userPageHistoryTracking);

    setTimeout(ajaxToken);

    setTimeout(hydrateFooter);
    import("./fixForBootstrapNative");
  });
};

main();
